import React, { useEffect } from "react";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { ThinLayout } from "../Layouts/ThinLayout";
import { ResponseEdit } from "../Components/Response/Edit";
import {
  ItemType,
  ItemRecipientType,
  PointsAllocationMode,
  ResponseSubjectType,
  Response as ResponseType,
  CampaignType,
  CampaignState,
} from "../types";
import { Lock, Publish, Schedule } from "@mui/icons-material";
import { BlingCheckmark } from "../Components/BlingCheckmark";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_RESPONSE,
  GET_USERS,
  ResponseResult,
  UPDATE_RESPONSE,
  UsersResult,
} from "../Queries";
import LicenseGate from "../Components/License/Gate";
import { useErrorBoundary } from "../Contexts/ErrorContext";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

export const UserResponse = () => {
  const [searchParams] = useSearchParams();
  const { setError } = useErrorBoundary();
  const [t] = useTranslation();

  // Decompose query params
  const key = searchParams.get("key");
  const responseKeyBlob = atob(key || "");
  const responseParts = responseKeyBlob.split(":");
  const responseId = responseParts[0];
  const responseToken = responseParts[1];

  //
  // Queries
  //

  const { loading: responseLoading, data: responseData } =
    useQuery<ResponseResult>(GET_RESPONSE, {
      variables: { pk: responseId },
      context: {
        headers: {
          Authorization: `JWT ${responseToken}`,
        },
      },
      onError: (error) => setError(t("errors.responses.get"), error),
    });

  const { loading: usersLoading, data: usersData } = useQuery<UsersResult>(
    GET_USERS,
    {
      variables: {
        filters: {},
      },
      context: {
        headers: {
          Authorization: `JWT ${responseToken}`,
        },
      },
      onError: (error) => setError(t("errors.users.list"), error),
    },
  );

  //
  // Effects
  //

  const response = responseData ? responseData.response : undefined;
  const campaignName = response?.campaignName;
  const campaignType = response?.campaignType;

  //
  // Mutations
  //

  const [updateResponse] = useMutation(UPDATE_RESPONSE, {
    // refetchQueries: ["GetResponse"],
    onError: (error) => setError(t("errors.responses.update"), error),
  });

  //
  // Effects
  //

  useEffect(() => {
    if (response?.submitted) {
      const audio = new Audio("/response-submitted-subtle.mp3");
      audio.play();
    }
  }, [response?.submitted]);

  const users = usersData ? usersData.users : undefined;
  const author =
    users !== undefined
      ? users.find((user) => response?.authorId === user.id)
      : undefined;

  const showLoading = responseLoading || usersLoading;
  const template = response ? response.form : null;
  const subjects =
    users !== undefined
      ? users.filter(
          (subject) =>
            (
              response?.responseSubjects.map((sr) => sr.subjectUserId) || []
            ).indexOf(subject.id) >= 0,
        )
      : [];

  if (!response || !author || !template) {
    return (
      <ThinLayout title="User Response" loading={showLoading}>
        {showLoading ? (
          <></>
        ) : (
          <>We could not find the response associated with this URL.</>
        )}
      </ThinLayout>
    );
  }

  if (response.submitted) {
    return (
      <ThinLayout title="">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            opacity: 1,
            animation: "fade 2s linear",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <BlingCheckmark
              active={false}
              completed={true}
              size="large"
              divProps={{ style: { marginBottom: 10 } }}
            />
            <Typography variant="h4">
              You&apos;re all done! Your submission has been received.
            </Typography>
          </Box>
        </Box>
      </ThinLayout>
    );
  }

  if (
    response.campaignType === CampaignType.Survey &&
    response.campaignState === CampaignState.Closed &&
    // FL-84: an instructor can reopen an individual student's response even
    // after the survey has closed — that student bypasses the closed screen.
    !response.reopened
  ) {
    return (
      <ThinLayout title="">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            opacity: 1,
            animation: "fade 2s linear",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Lock sx={{ mr: 2, mb: 2, fontSize: 60 }} />
            <Typography variant="h4">
              Sorry, this survey is closed to new responses.
            </Typography>
          </Box>
        </Box>
      </ThinLayout>
    );
  }

  if (
    response.campaignState === CampaignState.Scheduled ||
    response.campaignState === CampaignState.AssignmentScheduled
  ) {
    const sendDate = response.campaignAutoSendDate
      ? dayjs(response.campaignAutoSendDate).format("M/D/YYYY h:mm A")
      : "the scheduled date";
    const label =
      campaignType === CampaignType.Assignment ? "assignment" : "survey";
    return (
      <ThinLayout title="">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Schedule sx={{ mr: 2, mb: 2, fontSize: 60 }} />
            <Typography variant="h4">
              This {label} has not yet opened. Please check back on {sendDate}.
            </Typography>
          </Box>
        </Box>
      </ThinLayout>
    );
  }

  //
  // Event Handlers
  //

  async function handleResponseChange(response: ResponseType) {
    // Strip read-only fields from responseSubjects before sending mutation
    // ResponseSubjectInput only accepts: id, answers, completed, is_team_lead, subject_type, subject_user_id
    // Note: GraphQL converts camelCase to snake_case automatically, but we need to exclude read-only fields
    const responseSubjectsInput = response.responseSubjects.map((rs) => {
      const { subjectUserIsInstructor, subjectUserIsLearner, ...inputFields } =
        rs;
      return inputFields;
    });

    await updateResponse({
      variables: {
        id: response.id,
        submitted: response.submitted,
        submittedDate: new Date().toISOString(),
        responseSubjects: responseSubjectsInput,
      },
      context: {
        debounceKey: `response-${response.id}`,
        headers: {
          Authorization: `JWT ${responseToken}`,
        },
      },
      optimisticResponse: {
        updateResponse: {
          __typename: "Response",
          ...(response as any),
        },
      },
    });
  }

  function handleSubmit() {
    if (!response) {
      return;
    }
    handleResponseChange({ ...response, submitted: true });
  }

  // BEGIN POINTS VALIDATION
  const isAuthorInstructor = author.isInstructor === true;
  const allPeerSubjects = response.responseSubjects.filter(
    (rs) => rs.subjectType === ResponseSubjectType.Peer,
  );

  // Filter PT items to only those applicable to the author's role, mirroring
  // the recipient-type gate in isCompatible() and Edit.tsx's pointsItems filter.
  const pointsItems = template.items.filter((item) => {
    if (item.itemType !== ItemType.Points) return false;
    switch (item.recipientType) {
      case ItemRecipientType.TeamLeaders:
        return response.isTeamLead;
      case ItemRecipientType.NonLeaders:
        return !response.isTeamLead && !isAuthorInstructor;
      case ItemRecipientType.TeamInstructors:
        return isAuthorInstructor;
      case ItemRecipientType.TeamMembers:
      default:
        return !isAuthorInstructor;
    }
  });

  // All PT items score the same subjects: every non-instructor peer subject.
  // recipientType already determined which authors see the PT item (above);
  // it does not restrict which peers get scored.
  function getSubjectsForPointsItem(
    pi: (typeof pointsItems)[0],
  ): typeof allPeerSubjects {
    return allPeerSubjects.filter((rs) => rs.subjectUserIsInstructor !== true);
  }

  // Validate each PT item independently against its applicable subjects.
  const pointsValidated =
    pointsItems.length === 0 ||
    pointsItems.every((pi) => {
      const itemSubjects = getSubjectsForPointsItem(pi);
      const expectedTotal = (pi.scale ?? 0) * itemSubjects.length;
      const itemAnswers = itemSubjects
        .map((rs) => rs.answers.find((a) => a.itemId === pi.id))
        .map((a) => a?.value ?? 0);

      // All subjects must have an answer
      if (itemAnswers.length !== itemSubjects.length) return false;

      // Total must equal expected
      const total = itemAnswers.reduce((sum, v) => sum + v, 0);
      if (total !== expectedTotal) return false;

      // Minimum variance check
      if (pi.minVariance) {
        const variance = Math.max(...itemAnswers) - Math.min(...itemAnswers);
        if (variance < pi.minVariance) return false;
      }

      // Uniqueness check
      if (pi.pointsAllocationMode === PointsAllocationMode.UniqueAllocation) {
        const uniqueValues = new Set(itemAnswers);
        if (uniqueValues.size !== itemAnswers.length) return false;
      }

      return true;
    });
  // END POINTS VALIDATION

  const responsesMissing = response.responseSubjects.find(
    (sr) => !sr.completed,
  );
  const canSubmit = !responsesMissing && pointsValidated;

  return (
    <ThinLayout
      title={campaignName || "loading..."}
      loading={showLoading}
      fullWidth={campaignType === CampaignType.Assignment}
    >
      <LicenseGate jwt={responseToken}>
        <ResponseEdit
          response={response}
          author={author}
          subjects={subjects}
          formItems={template?.items || []}
          onChange={handleResponseChange}
          instructions={template.instructions}
          campaignType={campaignType}
          hideRecipientIndicators={
            campaignType === CampaignType.Assignment &&
            response.assignment?.submissionType === "LIV"
              ? false
              : undefined
          }
        />
        <Tooltip
          title={
            !canSubmit ? "All tabs must be completed before you can submit" : ""
          }
        >
          <span>
            <Button
              variant="contained"
              endIcon={<Publish />}
              onClick={handleSubmit}
              disabled={Boolean(!canSubmit)}
              sx={{ ml: 2 }}
            >
              Submit
            </Button>
          </span>
        </Tooltip>
      </LicenseGate>
    </ThinLayout>
  );
};
